import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AsmConfig, CustomCustomerSearchPage } from '@asm/const';
import { AsmService } from '@asm/feature';
import { PhoneFormatPipe } from '@sdvor/shared/ui';
import { User } from '@spartacus/core';
import { FormErrorsComponent } from '@spartacus/storefront';
import { Observable, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'ui-customer-selection',
  templateUrl: './customer-selection.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, ReactiveFormsModule, FormErrorsComponent, PhoneFormatPipe],
})
export class CustomCustomerSelectionComponent implements OnInit, OnDestroy {
  @Output()
  submitEvent = new EventEmitter<{ customerId?: string }>();

  @ViewChild('resultList') resultList: ElementRef;
  @ViewChild('searchTerm') searchTerm: ElementRef;

  searchResults: Observable<CustomCustomerSearchPage>;
  searchResultsLoading$: Observable<boolean>;
  selectedCustomer: User | undefined;
  customerSelectionForm: FormGroup;

  protected subscription = new Subscription();

  constructor(
    protected fb: FormBuilder,
    protected asmService: AsmService,
    protected config: AsmConfig,
  ) {}

  @HostListener('document:click', ['$event']) documentClick(event: Event): void {
    this.onDocumentClick(event);
  }

  ngOnInit(): void {
    this.customerSelectionForm = this.fb.group({
      searchTerm: ['', Validators.required],
    });
    this.asmService.customerSearchReset();
    this.searchResultsLoading$ = this.asmService.getCustomerSearchResultsLoading();
    this.searchResults = this.asmService.getCustomerSearchResults();

    this.subscription.add(
      this.customerSelectionForm.controls.searchTerm.valueChanges
        .pipe(debounceTime(300))
        .subscribe((searchTermValue) => {
          this.handleSearchTerm(searchTermValue);
        }),
    );
  }

  selectCustomerFromList(customer: User): void {
    this.selectedCustomer = customer;
    this.customerSelectionForm.controls.searchTerm.setValue(this.selectedCustomer.name);
    this.asmService.customerSearchReset();
  }

  onSubmit(): void {
    if (this.customerSelectionForm.valid && !!this.selectedCustomer) {
      this.submitEvent.emit({ customerId: this.selectedCustomer.customerId });
    } else {
      this.customerSelectionForm.markAllAsTouched();
    }
  }

  onDocumentClick(event: Event): void {
    if (this.resultList) {
      if (
        this.resultList.nativeElement.contains(event.target) ||
        this.searchTerm.nativeElement.contains(event.target)
      ) {
        return;
      } else {
        this.asmService.customerSearchReset();
      }
    }
  }

  closeResults(): void {
    this.asmService.customerSearchReset();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.asmService.customerSearchReset();
  }

  protected handleSearchTerm(searchTermValue: string): void {
    if (!!this.selectedCustomer && searchTermValue !== this.selectedCustomer.name) {
      this.selectedCustomer = undefined;
    }
    if (this.selectedCustomer) {
      return;
    }
    this.asmService.customerSearchReset();
    if (searchTermValue.trim().length >= 3) {
      this.asmService.customerSearch({
        query: searchTermValue,
        pageSize: this.config.asm?.customerSearch?.maxResults,
      });
    }
  }
}
